import { API_BASE } from '../env'
import { request } from './http'
import type { Schema } from './schema'

// ── Organizations, location groups and delegated user administration ─────────────────────────────
//
// Plans/delegated-user-management.md. These routes serve anyone holding `user.*` or `org.*`
// somewhere, not just administrators: the server decides, per user and per organization, what the
// caller may see and change, and answers 404 for anything they may not.

export type UserSummaryWire = Schema<'UserSummaryWire'>
export type UserPageWire = Schema<'UserPageWire'>
export type UserDetailWire = Schema<'UserDetailWire'>
export type UserGrantWire = Schema<'UserGrantWire'>
export type AssignableWire = Schema<'AssignableWire'>
export type AssignableBrandWire = Schema<'AssignableBrandWire'>
export type OrganizationWire = Schema<'OrganizationWire'>
export type OrganizationDetailWire = Schema<'OrganizationDetailWire'>
export type OrgGrantWire = Schema<'OrgGrantWire'>
export type LocationGroupWire = Schema<'LocationGroupWire'>
export type OrgSignInWire = Schema<'OrgSignInWire'>
export type SetOrgSignInRequest = Schema<'SetOrgSignInRequest'>

/** A role over a brand: the whole of it (all of the organization's stores there), or these groups and stores. */
export interface GrantScope {
  all_locations: boolean
  group_ids: string[]
  location_ids: string[]
}

export interface UserGrantRequest extends GrantScope {
  brand_id: string
  role_id: string
}

export interface OrgGrantRequest extends GrantScope {
  brand_id: string
  ceiling_role_id: string
}

export function listUsers(org?: string | null): Promise<UserPageWire> {
  const params = new URLSearchParams({ limit: '500' })
  if (org) params.set('org', org)
  const query = params.toString()
  return request<UserPageWire>('GET', `${API_BASE}/users?${query}`)
}

export function getUser(userId: string): Promise<UserDetailWire> {
  return request<UserDetailWire>('GET', `${API_BASE}/users/${userId}`)
}

export function createUser(body: {
  email: string
  name: string | null
  organization_id: string | null
  grants: UserGrantRequest[]
}): Promise<UserDetailWire> {
  return request<UserDetailWire>('POST', `${API_BASE}/users`, { json: body })
}

export function updateUser(
  userId: string,
  body: { name?: string | null; is_enabled?: boolean },
): Promise<UserDetailWire> {
  return request<UserDetailWire>('PUT', `${API_BASE}/users/${userId}`, { json: body })
}

/** Disables the account; nothing is deleted. */
export function disableUser(userId: string): Promise<UserDetailWire> {
  return request<UserDetailWire>('DELETE', `${API_BASE}/users/${userId}`)
}

export function setUserAccess(
  userId: string,
  brandId: string,
  body: GrantScope & { role_id: string },
): Promise<UserDetailWire> {
  return request<UserDetailWire>('PUT', `${API_BASE}/users/${userId}/access/${brandId}`, { json: body })
}

export function removeUserAccess(userId: string, brandId: string): Promise<UserDetailWire> {
  return request<UserDetailWire>('DELETE', `${API_BASE}/users/${userId}/access/${brandId}`)
}

export function getUserActivity(userId: string): Promise<Schema<'ActivityPageWire'>> {
  return request<Schema<'ActivityPageWire'>>('GET', `${API_BASE}/users/${userId}/activity`)
}

/** What the caller may give a member of `org` (their own organization when absent). */
export function getUserAssignable(org?: string | null): Promise<AssignableWire> {
  const params = new URLSearchParams()
  if (org) params.set('org', org)
  const query = params.toString()
  return request<AssignableWire>('GET', `${API_BASE}/users/assignable?${query}`)
}

export function listOrganizations(): Promise<Schema<'OrganizationListWire'>> {
  return request<Schema<'OrganizationListWire'>>('GET', `${API_BASE}/orgs`)
}

export function getOrganization(orgId: string): Promise<OrganizationDetailWire> {
  return request<OrganizationDetailWire>('GET', `${API_BASE}/orgs/${orgId}`)
}

export function createOrganization(body: {
  name: string
  kind: string
  parent_id: string | null
  grants: OrgGrantRequest[]
  allowed_email_domains?: string[] | null
}): Promise<OrganizationDetailWire> {
  return request<OrganizationDetailWire>('POST', `${API_BASE}/orgs`, { json: body })
}

export function updateOrganization(
  orgId: string,
  body: { name?: string | null; kind?: string | null; is_enabled?: boolean; allowed_email_domains?: string[] | null },
): Promise<OrganizationDetailWire> {
  return request<OrganizationDetailWire>('PUT', `${API_BASE}/orgs/${orgId}`, { json: body })
}

export function setOrganizationAccess(
  orgId: string,
  brandId: string,
  body: GrantScope & { ceiling_role_id: string },
): Promise<OrganizationDetailWire> {
  return request<OrganizationDetailWire>('PUT', `${API_BASE}/orgs/${orgId}/access/${brandId}`, { json: body })
}

export function removeOrganizationAccess(orgId: string, brandId: string): Promise<OrganizationDetailWire> {
  return request<OrganizationDetailWire>('DELETE', `${API_BASE}/orgs/${orgId}/access/${brandId}`)
}

/** What the caller may give an organization made beneath `parent` (their own when absent). */
export function getOrgAssignable(parent?: string | null): Promise<AssignableWire> {
  const params = new URLSearchParams()
  if (parent) params.set('parent', parent)
  const query = params.toString()
  return request<AssignableWire>('GET', `${API_BASE}/orgs/assignable?${query}`)
}

export function listLocationGroups(brandId: string): Promise<Schema<'LocationGroupListWire'>> {
  return request<Schema<'LocationGroupListWire'>>('GET', `${API_BASE}/brands/${brandId}/location-groups`)
}

export function createLocationGroup(
  brandId: string,
  body: { name: string; parent_id: string | null },
): Promise<LocationGroupWire> {
  return request<LocationGroupWire>('POST', `${API_BASE}/brands/${brandId}/location-groups`, { json: body })
}

export function updateLocationGroup(
  groupId: string,
  body: { name: string; parent_id: string | null },
): Promise<LocationGroupWire> {
  return request<LocationGroupWire>('PUT', `${API_BASE}/location-groups/${groupId}`, { json: body })
}

export function deleteLocationGroup(groupId: string): Promise<Schema<'SuccessWire'>> {
  return request<Schema<'SuccessWire'>>('DELETE', `${API_BASE}/location-groups/${groupId}`)
}

export function setLocationGroupMembers(groupId: string, locationIds: string[]): Promise<LocationGroupWire> {
  return request<LocationGroupWire>('PUT', `${API_BASE}/location-groups/${groupId}/members`, {
    json: { location_ids: locationIds },
  })
}

/** System administrators: move a user to another organization. */
export function adminMoveUser(userId: string, organizationId: string): Promise<UserDetailWire> {
  return request<UserDetailWire>('PUT', `${API_BASE}/admin/users/${userId}/organization`, {
    json: { organization_id: organizationId },
  })
}

/** System administrators: open (or close) an organization to delegated administration. */
export function adminSetDelegationLock(orgId: string, locked: boolean): Promise<OrganizationDetailWire> {
  return request<OrganizationDetailWire>('PUT', `${API_BASE}/admin/organizations/${orgId}/delegation`, {
    json: { locked },
  })
}

/** System administrators: give a Platform member one role on every brand and store, now and later. */
export function adminSetAllBrandsRole(userId: string, roleId: string): Promise<UserDetailWire> {
  return request<UserDetailWire>('PUT', `${API_BASE}/admin/users/${userId}/all-brands`, { json: { role_id: roleId } })
}

export function adminRemoveAllBrandsRole(userId: string): Promise<UserDetailWire> {
  return request<UserDetailWire>('DELETE', `${API_BASE}/admin/users/${userId}/all-brands`)
}

/**
 * Has the identity provider email the person a new link to finish setting up their account (verify the address,
 * choose a password, enroll a second factor where required).
 */
export function sendSetupEmail(userId: string): Promise<UserDetailWire> {
  return request<UserDetailWire>('POST', `${API_BASE}/users/${userId}/setup-email`)
}

/** Has the identity provider email the person a link to choose a new password. */
export function sendPasswordReset(userId: string): Promise<UserDetailWire> {
  return request<UserDetailWire>('POST', `${API_BASE}/users/${userId}/password-reset`)
}

export type TemporaryPasswordWire = Schema<'TemporaryPasswordWire'>

/**
 * A one-time password the relay makes for the person, returned this once, for an administrator to hand over while
 * email can't reach them. No email is sent; they choose their own password at their next sign-in.
 */
export function setTemporaryPassword(userId: string): Promise<TemporaryPasswordWire> {
  return request<TemporaryPasswordWire>('POST', `${API_BASE}/users/${userId}/temporary-password`)
}

/**
 * Ends every browser remembered for the person, so they give their authenticator code at the next sign-in on each
 * (Plans/sign-in-remembered-browsers.md). Anyone may do it for themselves.
 */
export function forgetRememberedBrowsers(userId: string): Promise<unknown> {
  return request<unknown>('POST', `${API_BASE}/users/${userId}/forget-browsers`)
}

/** System administrators: move users into an organization at once, and optionally open it to delegation. */
export function adminRehome(body: {
  user_ids: string[]
  organization_id: string
  open_organization: boolean
}): Promise<Schema<'RehomeResultWire'>> {
  return request<Schema<'RehomeResultWire'>>('POST', `${API_BASE}/admin/organizations/rehome`, { json: body })
}


// ── An organization's own identity provider ──────────────────────────────────────────────────────
//
// Plans/identity-consolidation.md, Phase 3b: proved email domains, the organization's SAML or OIDC
// provider, "require SSO", and group-to-role mappings. Someone above the organization holding
// `org.manage.sign_in` only; the client secret is write-only.

export function getOrgSignIn(orgId: string): Promise<OrgSignInWire> {
  return request<OrgSignInWire>('GET', `${API_BASE}/orgs/${orgId}/sign-in`)
}

export function setOrgSignIn(orgId: string, body: SetOrgSignInRequest): Promise<OrgSignInWire> {
  return request<OrgSignInWire>('PUT', `${API_BASE}/orgs/${orgId}/sign-in`, { json: body })
}

export function removeOrgSignIn(orgId: string): Promise<OrgSignInWire> {
  return request<OrgSignInWire>('DELETE', `${API_BASE}/orgs/${orgId}/sign-in`)
}

export function addOrgDomain(orgId: string, domain: string): Promise<OrgSignInWire> {
  return request<OrgSignInWire>('POST', `${API_BASE}/orgs/${orgId}/sign-in/domains`, { json: { domain } })
}

export function verifyOrgDomain(orgId: string, domain: string): Promise<OrgSignInWire> {
  return request<OrgSignInWire>('POST', `${API_BASE}/orgs/${orgId}/sign-in/domains/${encodeURIComponent(domain)}/verify`)
}

export function removeOrgDomain(orgId: string, domain: string): Promise<OrgSignInWire> {
  return request<OrgSignInWire>('DELETE', `${API_BASE}/orgs/${orgId}/sign-in/domains/${encodeURIComponent(domain)}`)
}
